import { useEffect, useRef } from "preact/hooks"

import type { WidgetConfig } from "@workspace/contracts/embed"
import {
  playSpotlights,
  spotlightLines,
} from "@workspace/contracts/embed/spotlight"

/**
 * What the chat variants (`chat.tsx`) and the Messenger preset
 * (`chat-messenger.tsx`) both lean on: the config type, the composer's
 * hint and the spotlights typed over it, the column that follows the newest
 * turn.
 */

export type ChatConfig = Extract<WidgetConfig, { type: "chat" }>

export const welcomeOf = (config: ChatConfig) =>
  config.welcome || "Hi! Ask me anything before you buy."

/**
 * The composer's RESTING hint: the placeholder, else `fallback` - the
 * surface's own words. What the field says when there are no spotlights;
 * when there are, `useSpotlights` types them over it.
 */
export function useComposerHint(
  config: ChatConfig,
  fallback = "Type your message…"
) {
  return config.placeholder || fallback
}

/**
 * The spotlights, typed into the composer (`@workspace/contracts/embed/
 * spotlight`: a letter at a time, the line held, then the next). Returns
 * whether there are any - the composer then leaves `placeholder` out of its
 * markup, since this owns it.
 *
 * Written to the field DIRECTLY, through its ref, and on purpose: a letter
 * every 40 ms through state would re-render the whole chat - the thread
 * included - twenty-five times a second on a visitor's phone, to change one
 * attribute. No state here, so nothing renders.
 *
 * A visitor who asked for less motion gets whole lines, swapped.
 */
export function useSpotlights(
  input: { current: HTMLInputElement | null },
  config: ChatConfig
): boolean {
  const lines = spotlightLines(config.spotlights)
  // The lines themselves, not their count: an edit that keeps the count
  // must still start over.
  const key = lines.join("\n")
  useEffect(() => {
    const field = input.current
    if (!field || lines.length === 0) return
    const reduced =
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false
    return playSpotlights(
      lines,
      (text) => {
        field.placeholder = text
      },
      { reduced }
    )
  }, [key])
  return lines.length > 0
}

/** Keeps the newest turn in view as the column grows. */
export function useStickToBottom(deps: unknown[]) {
  const column = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = column.current
    if (el) el.scrollTop = el.scrollHeight
  }, deps)
  return column
}
